import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import FilterItemSelect from '@/components/Filter/FilterItem/FilterItemSelect';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import useAgentSelect from '@/hooks/domain/useAgentSelect';
import { getAlphaColor } from '@/styles/colors/color.utils';
import {
  type CUSTOMER_QUERY_DATE_RANGE,
  customerQueryDateRangeDic,
  customerQueryDomainDic,
} from '@/types/customerConst';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import { track } from '@/utils/track/track';

import type { CustomerQueryState } from '../CustomerSentimentPage';
import {
  useDomainSelect,
  useRangeSelect,
  useSegmentSelect,
  useSegmentValueOptions,
} from '../hooks/useCustomerQueryOptions';

type Props = {
  state: CustomerQueryState;
  setState: (state: CustomerQueryState) => void;
  dateRange: CUSTOMER_QUERY_DATE_RANGE;
  setDateRange: (date: CUSTOMER_QUERY_DATE_RANGE) => void;
};

const CustomerFilterArea = (props: Props) => {
  const segmentSelectRef = useRef<HTMLDivElement>(null);
  const agentSelectOptions = useAgentSelect();
  const rangeSelectOptions = useRangeSelect();
  const domainSelectOptions = useDomainSelect();

  const [segmentValueState, setSegmentValueState] = useState<boolean>(false);

  const segmentSelectOptions = useSegmentSelect(props.state.domain);
  const segmentValueOptions = useSegmentValueOptions(props.state.segment);
  return (
    <Flex g={12} a="center">
      <Select
        value={props.dateRange}
        options={rangeSelectOptions}
        style={{ width: 140 }}
        onSelect={(v) => {
          track(AMP_KEYS.CUSTOMER_PERIOD_SELECT, {
            period: customerQueryDateRangeDic[v],
          });
          props.setDateRange(v);
        }}
      />
      <Select
        value={props.state.domain}
        options={domainSelectOptions}
        style={{ width: 140 }}
        onSelect={(v) => {
          track(AMP_KEYS.CUSTOMER_DOMAIN_SELECT, {
            domain: customerQueryDomainDic[v],
          });
          props.setState({
            ...props.state,
            domain: Number(v),
            segment: undefined,
          });
        }}
      />
      <Select
        allowClear
        showSearch
        onClear={() =>
          props.setState({
            ...props.state,
            agent: undefined,
          })
        }
        optionFilterProp="searchValue"
        placeholder="All agents"
        value={props.state.agent}
        options={agentSelectOptions}
        style={{ width: 200 }}
        onSelect={(v) =>
          props.setState({
            ...props.state,
            agent: v,
          })
        }
      />
      {!props.state.segment_value && (
        <VFlex ref={segmentSelectRef} style={{ position: 'relative' }}>
          <Select
            allowClear
            placeholder="All segments"
            value={props.state.segment}
            options={segmentSelectOptions}
            style={{ width: 160 }}
            onClear={() => {
              track(AMP_KEYS.CUSTOMER_SEGMENT_CLEAR);
              props.setState({
                ...props.state,
                segment: undefined,
                segment_value: undefined,
              });
            }}
            onSelect={(v) => {
              const segmentName = segmentSelectOptions.find(
                (op) => op.value === v,
              )?.label;
              track(AMP_KEYS.CUSTOMER_SEGMENT_SELECT, {
                segment: segmentName || '',
              });
              setSegmentValueState(true);
              props.setState({
                ...props.state,
                segment: v,
              });
            }}
          />
        </VFlex>
      )}
      {props.state.segment_value && (
        <CurrentSegementValueStyle
          ref={segmentSelectRef}
          a="center"
          onClick={() => setSegmentValueState(true)}
        >
          <Typo type="label3" ellipsis className="txt-value text-gray-700">
            {props.state.segment_value.label}
          </Typo>
          <Flex className="btn-action">
            <DownOutlined className="ic-down" />
            <CloseCircleFilled
              className="ic-clear"
              onClick={(e) => {
                e.stopPropagation();
                props.setState({
                  ...props.state,
                  segment: undefined,
                  segment_value: undefined,
                });
              }}
            />
          </Flex>
        </CurrentSegementValueStyle>
      )}
      <FilterItemSelect
        status={segmentValueState}
        filterOption={segmentValueOptions}
        currFilterQueryFormat={{
          columnKey: props.state.segment || '',
          filterValues: [],
          operator: FILTER_OPERATOR.OR,
        }}
        close={() => setSegmentValueState(false)}
        onSelect={(filterValue) => {
          const segmentLabel = segmentSelectOptions.find(
            (op) => op.value === props.state.segment,
          )?.label;
          props.setState({
            ...props.state,
            segment_value: {
              value: filterValue.filterKey?.toString() || '',
              label: `${segmentLabel} / ${filterValue.filterName}`,
            },
          });
          setSegmentValueState(false);
        }}
        triggerEl={segmentSelectRef.current}
        modalOptions={{
          offset: {
            y: 2,
          },
        }}
      />
    </Flex>
  );
};

const CurrentSegementValueStyle = styled(Flex)`
  position: relative;
  width: 160px;
  padding-left: 12px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s;
  align-self: center;
  &:hover {
    border: 1px solid ${(props) => props.theme.colors.primary100};
  }
  .txt-value {
    width: calc(100% - 24px);
    transform: translateY(1px);
  }
  .ic-down {
    position: absolute;
    opacity: 1;
    transition: opacity 0.3s;
    top: 50%;
    transform: translateY(-50%);
    color: ${(props) => props.theme.colors.gray600};
  }
  .ic-clear {
    position: absolute;
    opacity: 0;
    transition: all 0.3s;
    top: 50%;
    transform: translateY(-50%);
    color: ${(props) => getAlphaColor(props.theme.colors.gray800, 0.25)};
  }
  &:hover {
    .ic-down {
      opacity: 0;
    }
    .ic-clear {
      opacity: 1;
      z-index: 1;
      &:hover {
        color: ${(props) => getAlphaColor(props.theme.colors.gray800, 0.5)};
      }
    }
  }
  .btn-action {
    position: absolute;
    top: 50%;
    right: 22px;
  }
`;
export default CustomerFilterArea;
